// Pathway intros browser — lists every pathway with a "Open Visual Editor"
// button so admins can edit each intro without hunting through the
// Pathways admin panel. Reuses `PageSectionsEditor` and `defaultPathwayIntroSections`.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PencilLine, Eye, Route as RouteIcon, EyeOff } from "lucide-react";
import PageSectionsEditor from "@/components/PageSectionsEditor";
import { defaultPathwayIntroSections } from "@/components/sectionTypes";

export default function PathwayIntrosPanel() {
  const [pathways, setPathways] = useState(null);
  const [editor, setEditor] = useState({ open: false, pathway: null, sections: [] });

  const load = () =>
    api.get("/pathways?include_hidden=true")
       .then(r => setPathways(r.data))
       .catch(() => setPathways([]));
  useEffect(() => { load(); }, []);

  const openEditor = async (p) => {
    try {
      const r = await api.get(`/pathways/${p.slug}`);
      setEditor({ open: true, pathway: p, sections: r.data.intro_sections || [] });
    } catch {
      setEditor({ open: true, pathway: p, sections: [] });
    }
  };
  const saveIntro = async (next) => {
    if (!editor.pathway) return;
    await api.put(`/pathways/${editor.pathway.pathway_id}`, { intro_sections: next });
    load();
  };

  if (pathways === null) return <div className="mt-6 text-slate-500">Loading…</div>;

  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <RouteIcon className="w-4 h-4 text-sky-700"/>
          Pathway intros · Visual Editor
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          Compose the intro block shown above the reading list on each pathway page. Same block editor as the home and blog editors.
        </p>

        {pathways.length === 0 ? (
          <div className="text-sm text-slate-500 italic text-center py-6 border border-dashed border-slate-300 rounded-md">
            No pathways yet. Create one in <strong>Content → Pathways</strong> first.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {pathways.map(p => {
              const count = p.intro_sections?.length || 0;
              return (
                <div key={p.pathway_id} data-testid={`pathway-intro-row-${p.pathway_id}`}
                     className="border border-slate-200 rounded-md p-4 flex flex-col bg-slate-50/50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{p.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {count > 0
                          ? <>{count} intro section{count === 1 ? "" : "s"} saved</>
                          : <span className="italic">Using default (title + description)</span>}
                      </div>
                    </div>
                    {!p.visible && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-slate-200 text-slate-600 rounded px-1 py-0.5 shrink-0">
                        <EyeOff className="w-2.5 h-2.5"/>Hidden
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Button data-testid={`pathway-intro-open-${p.pathway_id}`} size="sm"
                            className="bg-sky-700 hover:bg-sky-600 h-8"
                            onClick={() => openEditor(p)}>
                      <PencilLine className="w-3.5 h-3.5 mr-1.5"/>Open Visual Editor
                    </Button>
                    <a href={`/pathways/${p.slug}`} target="_blank" rel="noopener noreferrer"
                       className="text-xs uppercase tracking-widest text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
                      <Eye className="w-3 h-3"/>Preview
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <PageSectionsEditor
        open={editor.open}
        onOpenChange={(v) => setEditor(prev => ({ ...prev, open: v }))}
        title={editor.pathway ? `Edit intro — ${editor.pathway.title}` : "Edit pathway intro"}
        resourceKey={editor.pathway ? `pathway_intro:${editor.pathway.pathway_id}` : null}
        initialSections={editor.sections.length ? editor.sections : defaultPathwayIntroSections(editor.pathway)}
        onSave={saveIntro}
      />
    </Card>
  );
}
